import type {
  ChatPreset,
  ChatPresetInput,
  ChatStrategies,
  BuiltinChatPresetName,
  MarkdownExtension,
  UploadRequestFn,
} from "@/types";
import { baseChatStrategies, basePreset } from "./base";
import { xGroupAdkPreset } from "./xgroup/preset";

export const DEFAULT_CHAT_PRESET_NAME = "xgroup-adk" as const;

/** SDK 内建预设注册表。外部预设应优先直接传入 ChatPreset 对象。 */
export const BUILTIN_CHAT_PRESETS = Object.freeze({
  base: basePreset,
  "xgroup-adk": xGroupAdkPreset,
}) satisfies Readonly<Record<BuiltinChatPresetName, ChatPreset>>;

/**
 * 统一解析内建名称和自定义预设对象。
 * 未传时继续使用 xgroup-adk，保持既有接入行为。
 */
export const resolveChatPreset = (
  preset: ChatPresetInput | undefined,
): ChatPreset => {
  if (!preset) return BUILTIN_CHAT_PRESETS[DEFAULT_CHAT_PRESET_NAME];
  if (typeof preset !== "string") return preset;
  return BUILTIN_CHAT_PRESETS[preset];
};

/** 将基础策略、预设策略和接入方局部覆盖按优先级合并。 */
export const mergeChatStrategies = (
  preset: ChatPresetInput | undefined,
  overrides?: ChatStrategies,
): ChatStrategies => ({
  ...baseChatStrategies,
  ...resolveChatPreset(preset).strategies,
  ...overrides,
});

/** 合并预设与接入方 Markdown 扩展；同名标签始终由接入方覆盖。 */
export const resolvePresetMarkdownExtensions = (
  preset: ChatPresetInput | undefined,
  overrides: readonly MarkdownExtension[] = [],
) => {
  const registeredTags = new Set<string>();
  return [
    ...overrides,
    ...(resolveChatPreset(preset).markdownExtensions || []),
  ].filter((extension) => {
    const tagName = extension.tagName.toLowerCase();
    if (registeredTags.has(tagName)) return false;
    registeredTags.add(tagName);
    return true;
  });
};

/** 解析当前预设提供的默认上传实现，显式 uploadRequest 仍由调用方优先。 */
export const resolvePresetUploadRequest = (
  preset: ChatPresetInput | undefined,
  context: {
    url: string;
    token?: string;
    appNo?: string;
  },
): UploadRequestFn | undefined =>
  resolveChatPreset(preset).createUploadRequest?.(context);
